import { memo, useCallback, useMemo, useState } from "react";
import { StyleSheet } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { FormInput } from "@src/components/FormInput";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { capitalize } from "@src/utils/capitalize";
import { isWeb } from "@src/utils/web-helpers";

export type CreateUserRole = "manager" | "viewer";

export type CreateUserFormData = {
  email: string;
  role: CreateUserRole;
};

interface CreateUserFormProps {
  initialEmail?: string;
  onSave: (data: CreateUserFormData) => void;
  saveLabel?: string;
}

export const CreateUserForm = memo(({ initialEmail, onSave, saveLabel = "Invite" }: CreateUserFormProps) => {
  const [email, setEmail] = useState(initialEmail ?? "");
  const [role, setRole] = useState<CreateUserRole>("manager");
  const [errors, setErrors] = useState<{ email?: string }>({});

  const options = useMemo(() => [{ title: "Manager" }, { title: "Viewer" }], []);

  const handleRoleChange = useCallback((option: string) => {
    setRole(option.toLowerCase() as CreateUserRole);
  }, []);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    if (value.trim()) setErrors((prev) => ({ ...prev, email: undefined }));
  }, []);

  const handleSave = useCallback(() => {
    const newErrors: { email?: string } = {};
    if (!email.trim()) newErrors.email = "Enter email address";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({ email, role });
    setErrors({});
  }, [email, role, onSave]);

  return (
    <View>
      <View gap="3xl">
        <FormInput
          placeholder="Email address"
          value={email}
          onChangeText={handleEmailChange}
          size="medium"
          keyboardType="email-address"
          autoCapitalize="none"
          error={errors.email}
        />
      </View>

      <View flexDirection="row" alignItems="center" justifyContent="space-between" style={styles.roleRow}>
        <Text textStyle="sm/regular">Role</Text>
        <SegmentedSelector
          optionsLabels={options}
          selectedOption={capitalize(role)}
          onSelectOption={handleRoleChange}
          containerStyle={styles.roleSelectorContainer}
          activeBoxStyle={styles.roleSelectorActiveBox}
          width={isWeb ? 200 : undefined}
        />
      </View>

      <Button variant="primary" label={saveLabel} size="md" onPress={handleSave} />
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  roleRow: {
    marginTop: theme.spacing["3xl"],
    marginBottom: theme.spacing["4xl"],
  },
  roleSelectorContainer: {
    backgroundColor: "transparent",
  },
  roleSelectorActiveBox: {
    backgroundColor: theme.colors.surface2.hover,
  },
}));
